import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Globe, Sparkles, Flame } from 'lucide-react';
import type { Announcement } from '@/types/announcement';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const meterCopy = {
  en: { messagesShared: 'Messages Shared', relationshipsStrengthened: 'Relationships Strengthened', impactPoints: 'Impact Points', relationshipMomentum: 'Relationship Momentum', weekStreak: '-Week Connection Streak', loveGrows: 'Love grows when you say it.', daysAgo: 'd ago', reconnect: 'Ready to reconnect?' },
  es: { messagesShared: 'Mensajes Compartidos', relationshipsStrengthened: 'Relaciones Fortalecidas', impactPoints: 'Puntos de Impacto', relationshipMomentum: 'Impulso Relacional', weekStreak: ' Semanas de Racha', loveGrows: 'El amor crece cuando lo dices.', daysAgo: 'd atrás', reconnect: '¿Listo para reconectar?' },
  fr: { messagesShared: 'Messages Partagés', relationshipsStrengthened: 'Relations Renforcées', impactPoints: "Points d'Impact", relationshipMomentum: 'Élan Relationnel', weekStreak: ' Semaines de Suite', loveGrows: "L'amour grandit quand on l'exprime.", daysAgo: 'j', reconnect: 'Prêt à reconnecter ?' },
  pt: { messagesShared: 'Mensagens Compartilhadas', relationshipsStrengthened: 'Relacionamentos Fortalecidos', impactPoints: 'Pontos de Impacto', relationshipMomentum: 'Impulso Relacional', weekStreak: ' Semanas Consecutivas', loveGrows: 'O amor cresce quando você o expressa.', daysAgo: 'd atrás', reconnect: 'Pronto para reconectar?' },
  ar: { messagesShared: 'رسائل مشتركة', relationshipsStrengthened: 'علاقات تعززت', impactPoints: 'نقاط التأثير', relationshipMomentum: 'زخم العلاقات', weekStreak: ' أسابيع متتالية', loveGrows: 'الحب ينمو عندما تعبّر عنه.', daysAgo: 'يوم', reconnect: 'مستعد للتواصل مجدداً؟' },
  ja: { messagesShared: '共有メッセージ', relationshipsStrengthened: '深まった関係', impactPoints: 'インパクトポイント', relationshipMomentum: '関係のモメンタム', weekStreak: '週連続', loveGrows: '言葉にすると愛は育ちます。', daysAgo: '日前', reconnect: 'つながり直しませんか？' },
  ko: { messagesShared: '공유된 메시지', relationshipsStrengthened: '강화된 관계', impactPoints: '임팩트 포인트', relationshipMomentum: '관계 모멘텀', weekStreak: '주 연속', loveGrows: '말할 때 사랑은 자랍니다.', daysAgo: '일 전', reconnect: '다시 연결할 준비가 되셨나요?' },
  id: { messagesShared: 'Pesan Dibagikan', relationshipsStrengthened: 'Hubungan Diperkuat', impactPoints: 'Poin Dampak', relationshipMomentum: 'Momentum Hubungan', weekStreak: ' Minggu Beruntun', loveGrows: 'Cinta tumbuh saat kamu mengungkapkannya.', daysAgo: 'h lalu', reconnect: 'Siap terhubung kembali?' },
};

interface ConnectionGrowthMeterProps {
  announcements: Announcement[];
  quietMode?: boolean;
}

function AnimatedCounter({ target, duration = 1.5 }: {target: number;duration?: number;}) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (hasAnimated.current) return;
    hasAnimated.current = true;

    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [target, duration]);

  return <span ref={ref}>{count}</span>;
}

function RelationshipRing({ name, daysSince, index, tooltipCopy }: {name: string;daysSince: number;index: number;tooltipCopy: {loveGrows: string;daysAgo: string;reconnect: string;};}) {
  const getColor = () => {
    if (daysSince <= 14) return 'stroke-primary';
    if (daysSince <= 30) return 'stroke-amber-500';
    return 'stroke-red-400/60';
  };

  const getGlow = () => {
    if (daysSince <= 14) return 'shadow-[0_0_12px_hsl(var(--primary)/0.3)]';
    if (daysSince <= 30) return 'shadow-[0_0_8px_rgba(245,158,11,0.2)]';
    return '';
  };

  const progress = Math.max(0, Math.min(1, 1 - daysSince / 60));
  const circumference = 2 * Math.PI * 18;
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <motion.div
      className="relative group cursor-default"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.1, duration: 0.4 }}>
      
      <div className={`relative rounded-full ${getGlow()}`}>
        <svg width="48" height="48" viewBox="0 0 48 48">
          <circle cx="24" cy="24" r="18" fill="none" className="stroke-muted/30" strokeWidth="3" />
          <motion.circle
            cx="24" cy="24" r="18"
            fill="none"
            className={getColor()}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset }}
            transition={{ duration: 1.2, delay: index * 0.15, ease: 'easeOut' }}
            transform="rotate(-90 24 24)" />
          
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-bold text-foreground">{name[0]}</span>
        </div>
      </div>
      {/* Tooltip */}
      <div className="absolute -top-10 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
        <div className="bg-popover text-popover-foreground text-xs rounded-lg px-3 py-1.5 shadow-lg whitespace-nowrap border border-border">
          {daysSince <= 14 ? tooltipCopy.loveGrows : daysSince <= 30 ? `${name} — ${daysSince}${tooltipCopy.daysAgo}` : tooltipCopy.reconnect}
        </div>
      </div>
    </motion.div>);

}

export function ConnectionGrowthMeter({ announcements, quietMode }: ConnectionGrowthMeterProps) {
  const { language } = useLanguage();
  const t = lc(meterCopy, language);

  if (quietMode) return null;

  const published = announcements.filter((a) => a.status === 'approved');
  const uniqueRecipients = new Set(published.map((a) => a.recipient_name));
  const totalPoints = published.length * 15 + uniqueRecipients.size * 10;

  // Calculate streak (weeks with at least one published announcement)
  const now = new Date();
  let streakWeeks = 0;
  for (let w = 0; w < 52; w++) {
    const weekStart = new Date(now.getTime() - (w + 1) * 7 * 86400000);
    const weekEnd = new Date(now.getTime() - w * 7 * 86400000);
    const hasAnnouncement = published.some((a) => {
      const d = new Date(a.created_at);
      return d >= weekStart && d < weekEnd;
    });
    if (hasAnnouncement) streakWeeks++;else
    break;
  }

  // Get recipient rings data
  const recipientData = Array.from(uniqueRecipients).slice(0, 6).map((name) => {
    const latest = published.filter((a) => a.recipient_name === name).
    sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
    const daysSince = Math.floor((now.getTime() - new Date(latest.created_at).getTime()) / 86400000);
    return { name, daysSince };
  });

  return (
    <div className="space-y-6">
      {/* Living Impact Counter */}
      <motion.div
        className="glass rounded-2xl p-6 border border-primary/15"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}>
        
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Heart className="h-4 w-4 text-primary" fill="currentColor" />
              <span className="text-2xl font-bold text-foreground">
                <AnimatedCounter target={published.length} />
              </span>
            </div>
            <p className="text-xs text-muted-foreground">{t.messagesShared}</p>
          </div>
          <div>
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Globe className="h-4 w-4 text-primary" />
              <span className="text-2xl font-bold text-foreground">
                <AnimatedCounter target={uniqueRecipients.size} />
              </span>
            </div>
            <p className="text-xs text-muted-foreground">{t.relationshipsStrengthened}</p>
          </div>
          <div>
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-2xl font-bold text-foreground">
                <AnimatedCounter target={totalPoints} />
              </span>
            </div>
            <p className="text-xs text-muted-foreground">{t.impactPoints}</p>
          </div>
        </div>
      </motion.div>

      {/* Relationship Momentum Rings */}
      {recipientData.length > 0













      }

      {/* Emotional Streak */}
      {streakWeeks > 0 &&
      <motion.div
        className="flex items-center justify-center gap-2 text-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}>
        
          <Flame className="h-4 w-4 text-amber-500" />
          <span className="font-semibold text-foreground">{streakWeeks}{t.weekStreak}</span>
        </motion.div>
      }
    </div>);

}